import { CopyFilled, RobotOutlined, SignatureFilled } from '@ant-design/icons';
import { RichiestaModuloDTOVerificaFirmaAutografaAi } from '@framework/api-client';
import { useTranslation } from '@framework/i18n';
import { FlexRow, PercentageFormatter } from '@framework/ui';
import clx from 'classnames';
import { darken } from 'polished';
import styled from 'styled-components';
import { AiMessageContainer } from './message/AiMessageContainer';
import { AiMessageText } from './message/AiMessageText';
import { getSignaturesSum } from './message/getSignaturesSum';

const AiSignatureChekWrapper = styled.div`
  margin-top: 16px;
  border-radius: 5px;
  padding: 4px 10px;
  background-color: ${props => props.theme.colorPrimaryBg};
  border: 2px solid ${props => props.theme.colorPrimary};
  color: ${props => darken(0.1, props.theme.colorPrimaryActive)};
  &.success {
    background-color: ${props => props.theme.colorSuccessBg};
    border: 2px solid ${props => props.theme.colorSuccess};
    color: ${props => darken(0.1, props.theme.colorSuccessActive)};
  }
`;

const AiIcon = styled.div`
  color: ${props => props.theme.colorInfo};
  font-size: 36px;
  &.is-backoffice {
    font-size: 28px;
  }
  &.success {
    color: ${props => props.theme.colorSuccess};
  }
`;

export interface IAiModuleCheckMessageProps {
  result?: RichiestaModuloDTOVerificaFirmaAutografaAi;
  isBackoffice?: boolean;
  isDigitallySigned?: boolean | null;
}

/**
 * Visualizzazione del risultato del test AI sul modulo
 */
export function AiModuleCheckMessage(props: IAiModuleCheckMessageProps) {
  const { result, isBackoffice, isDigitallySigned } = props;
  const { t } = useTranslation();

  if (!result) return null;

  // prepariamo i messaggi da mostrare in base al risultato
  const success = result.flag_similarity && result.flag_signatures;
  const signaturesCount = isBackoffice
    ? `(Firme individuate: ${getSignaturesSum(
        result.signatures_by_page ?? [],
      )}).`
    : '';
  const similarityScore = isBackoffice
    ? `(Similitudine: ${PercentageFormatter.format(
        result.similarity_score ?? 0,
      )}).`
    : '';
  const frontMessage = {
    notSimilarity: t(
      'ai-module-check-message.warning.not-similatity',
      'Il modulo caricato sembra differente da quello scaricato.',
    ),
    notSignatures: t(
      'ai-module-check-message.warning.not-signatures',
      'Il modulo non sembra firmato correttamente.',
    ),
  };
  // nel backoffice mostriamo le traduzioni
  const messages = {
    notSimilarity: `${frontMessage.notSimilarity} ${similarityScore}`,
    notSignatures: `${frontMessage.notSignatures} ${signaturesCount}`,
    similarity: `Il modulo caricato è corretto. ${similarityScore}`,
    signatures: `Il modulo è firmato correttamente.`,
    digitallySigned: t(
      'ai-module-check-message.success.digitally-signed',
      'Modulo firmato digitalmente.',
    ),
  };

  // se non siamo in backoffice e il modulo è corretto, non mostriamo nulla
  if (!isBackoffice && success) {
    return null;
  }

  return (
    <AiSignatureChekWrapper className={clx({ success })}>
      <FlexRow
        gutter={12}
        align="center"
        left={
          <AiIcon className={clx({ success, 'is-backoffice': isBackoffice })}>
            <RobotOutlined aria-hidden />
          </AiIcon>
        }
      >
        <AiMessageContainer isBackoffice={isBackoffice}>
          <AiMessageText
            isBackoffice={isBackoffice}
            success={!!result.flag_similarity}
            warningText={messages.notSimilarity}
            successText={messages.similarity}
            icon={<CopyFilled aria-hidden />}
          />
          <AiMessageText
            isBackoffice={isBackoffice}
            success={!!result.flag_signatures}
            warningText={
              isDigitallySigned
                ? messages.digitallySigned
                : messages.notSignatures
            }
            successText={isDigitallySigned ? undefined : messages.signatures}
            icon={<SignatureFilled aria-hidden />}
          />

          {/* solo in compilazione se ci sono warning */}
          {!isBackoffice && !success && (
            <AiMessageText
              warningText={t(
                'ai-module-check-message.warning.suggestions',
                'Per ricaricare il modulo, ritorna in compilazione.',
              )}
            />
          )}
        </AiMessageContainer>
      </FlexRow>
    </AiSignatureChekWrapper>
  );
}
